import type { SiegeView } from '@wyn/contracts';
import { useEffect, useRef, useState } from 'react';
import { palette, withAlpha } from '../lib/theme.ts';
import type { TurnGameProps } from './registry.tsx';

const W = 340, H = 210, GY = 190, SX = 310 / 300, SY = 1.35;
const X = (m: number) => 18 + m * SX;
const Y = (m: number) => GY - m * SY;

/**
 * The Siege Works. Set the counterweight and release angle and loose: the server flies the stone and
 * returns its path, which is drawn point by point. Earlier stones stay drawn so you can adjust.
 */
export function SiegeGame({ view, busy, onMove }: TurnGameProps<SiegeView>) {
  const [weight, setWeight] = useState(5);
  const [angle, setAngle] = useState(45);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [flying, setFlying] = useState(false);
  const shown = useRef(view.trails.length);

  // Draw the scene; when a new stone arrives, fly it in.
  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const d = window.devicePixelRatio || 1;
    c.width = W * d;
    c.height = H * d;
    const g = c.getContext('2d')!;
    g.setTransform(d, 0, 0, d, 0, 0);
    const fresh = view.trails.length > shown.current;
    // Before the newest stone lands, the wall stands as it was.
    const hpBefore = fresh ? Math.min(view.maxHp, view.hp + (view.last?.hit ?? 0)) : view.hp;
    const old = fresh ? view.trails.slice(0, -1) : view.trails;
    const cur = fresh ? view.trails[view.trails.length - 1]! : null;
    let n = 0, raf = 0;
    const frame = () => {
      draw(g, view, old, cur ? cur.slice(0, n + 1) : null, cur && n < cur.length - 1 ? hpBefore : view.hp);
      if (cur && n < cur.length - 1) {
        n++;
        raf = requestAnimationFrame(frame);
      } else {
        // Recorded only once it has flown, so a re-run of this effect (StrictMode) replays the flight.
        shown.current = view.trails.length;
        setFlying(false);
      }
    };
    if (cur) setFlying(true);
    frame();
    return () => cancelAnimationFrame(raf);
  }, [view]);

  const fire = () => {
    if (busy || flying || view.over) return;
    onMove({ type: 'loose', weight: Math.round(weight * 10) / 10, angle });
  };
  const fireRef = useRef(fire);
  fireRef.current = fire;
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) fireRef.current();
      else if (e.code === 'ArrowUp') setAngle((a) => Math.min(75, a + 1));
      else if (e.code === 'ArrowDown') setAngle((a) => Math.max(15, a - 1));
      else if (e.code === 'ArrowRight') setWeight((w) => Math.min(10, Math.round((w + 0.1) * 10) / 10));
      else if (e.code === 'ArrowLeft') setWeight((w) => Math.max(1, Math.round((w - 0.1) * 10) / 10));
      else return;
      e.preventDefault();
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, []);

  const last = view.last;
  const verdict = !last ? null : last.hit === 2 ? 'Battlements smashed! (2)' : last.hit === 1 ? 'Hit the wall! (1)' : last.short ? 'Short!' : 'Over the wall!';
  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-dim">
        Heavier counterweight throws harder. Around 45° throws furthest. Wind pushes the stone in flight. Upper-wall hits do double damage. Your earlier stones stay drawn so you can adjust.
      </p>
      <canvas ref={canvas} className="mx-auto block aspect-[34/21] w-full max-w-[340px] border border-rule" aria-label="The wall and your stones" />
      <div className="flex flex-wrap justify-between gap-2 font-mono text-sm">
        <span>Wind {view.wind < 0 ? '←' : '→'} {Math.abs(view.wind).toFixed(1)}</span>
        <span>Wall {view.hp}/{view.maxHp} · {Math.round(view.wallX)}m</span>
        <span>Stone {Math.min(view.shot + 1, view.shots)}/{view.shots}</span>
      </div>
      {verdict && !flying && <p role="status" className={`text-center text-sm ${last!.hit ? 'text-verdigris' : 'text-blood'}`}>{verdict}</p>}
      <label className="flex items-center gap-3 text-sm">
        <span className="w-28">Weight <b className="font-mono">{weight.toFixed(1)}t</b></span>
        <input type="range" className="flex-1 accent-gilt" min={1} max={10} step={0.1} value={weight} onChange={(e) => setWeight(Number(e.target.value))} aria-label="Counterweight" />
      </label>
      <label className="flex items-center gap-3 text-sm">
        <span className="w-28">Angle <b className="font-mono">{angle}°</b></span>
        <input type="range" className="flex-1 accent-gilt" min={15} max={75} step={1} value={angle} onChange={(e) => setAngle(Number(e.target.value))} aria-label="Release angle" />
      </label>
      <button type="button" className="btn w-full py-3" disabled={busy || flying || view.over} onClick={fire}>
        🪨 Loose the stone
      </button>
    </div>
  );
}

function draw(g: CanvasRenderingContext2D, v: SiegeView, old: [number, number][][], cur: [number, number][] | null, hp: number) {
  const c = palette();
  g.fillStyle = '#23262b';
  g.fillRect(0, 0, W, H);
  g.fillStyle = '#3a4a2c';
  g.fillRect(0, GY, W, H - GY);
  // The trebuchet.
  g.fillStyle = c['scene-wood'];
  g.fillRect(X(0) - 6, GY - 22, 12, 22);
  g.fillRect(X(0) - 2, GY - 40, 4, 22);
  // The wall, crumbling as it takes damage.
  const fr = Math.max(0, Math.min(1, hp / v.maxHp)), wh = v.wallHeight * Math.max(fr, 0.15);
  g.fillStyle = '#6f6a60';
  g.fillRect(X(v.wallX), Y(wh), v.wallWidth, wh * SY);
  if (fr > 0.5) for (let i = 0; i < 3; i++) g.fillRect(X(v.wallX) + i * 3, Y(v.wallHeight * fr) - 4, 2, 4);
  g.strokeStyle = 'rgba(0,0,0,.4)';
  g.beginPath();
  g.moveTo(X(v.wallX), Y(wh * v.upper));
  g.lineTo(X(v.wallX) + v.wallWidth, Y(wh * v.upper));
  g.stroke();
  for (const tr of old) {
    g.strokeStyle = withAlpha(c.ink, 0.18);
    g.beginPath();
    for (const [x, y] of tr) g.lineTo(X(x), Y(y));
    g.stroke();
  }
  if (cur && cur.length) {
    g.strokeStyle = withAlpha(c.ember, 0.8);
    g.beginPath();
    for (const [x, y] of cur) g.lineTo(X(x), Y(y));
    g.stroke();
    const [x, y] = cur[cur.length - 1]!;
    g.fillStyle = '#ccc';
    g.beginPath();
    g.arc(X(x), Y(y), 3.5, 0, Math.PI * 2);
    g.fill();
  }
}
